import { useState } from 'react';
import { motion } from 'framer-motion';
import { customEasing } from '../lib/easing';
import { getSourceReferenceUrl, isDoi, researchLibrary } from '../data/researchLibrary';

const categories = [
  { id: 'all', label: 'All Research', count: researchLibrary.length },
  { id: 'asi-original', label: 'ASI Original', count: researchLibrary.filter(r => r.category === 'asi-original').length },
  { id: 'productivity', label: 'Productivity', count: researchLibrary.filter(r => r.category === 'productivity').length },
  { id: 'enterprise', label: 'Enterprise Adoption', count: researchLibrary.filter(r => r.category === 'enterprise').length },
  { id: 'legal', label: 'Legal / High-Stakes', count: researchLibrary.filter(r => r.category === 'legal').length },
  { id: 'infrastructure', label: 'Privacy & Infrastructure', count: researchLibrary.filter(r => r.category === 'infrastructure').length },
  { id: 'benchmarks', label: 'Benchmarks & Multi-Model', count: researchLibrary.filter(r => r.category === 'benchmarks').length },
  { id: 'labor-market', label: 'Labor Market', count: researchLibrary.filter(r => r.category === 'labor-market').length },
];

// Research Library Section
function ResearchLibrary() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 8;
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setVisibleCount(pageSize);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setVisibleCount(pageSize);
  };

  const clearFilters = () => {
    setActiveCategory('all');
    setSearchQuery('');
    setVisibleCount(pageSize);
  };

  const filteredResearch = researchLibrary.filter((entry) => {
    const matchesCategory = activeCategory === 'all' || entry.category === activeCategory;
    const matchesSearch = searchQuery === '' ||
      entry.displayTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.abstract.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const visibleResearch = filteredResearch.slice(0, visibleCount);
  const hasMoreResults = visibleCount < filteredResearch.length;
  const isBrowseMode = activeCategory === 'all' && searchQuery.trim() === '';
  const selectedOriginals = researchLibrary
    .filter((entry) => entry.category === 'asi-original')
    .slice(0, 4);
  const browseSections = categories
    .filter((cat) => cat.id !== 'all')
    .map((cat) => ({
      category: cat,
      entries: researchLibrary.filter((entry) => entry.category === cat.id).slice(0, 4),
    }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: customEasing.smooth },
    },
  };

  return (
    <section
      id="research"
      className="section-asi bg-[#f7f8f6]"
    >
      {/* Enhanced ScholarlyArticle Schemas for AI crawlers */}
      {researchLibrary.map((entry, index) => (
        <script
          key={entry.id}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ScholarlyArticle',
              '@id': `https://aistudies.institute/#research-${entry.id}`,
              headline: entry.displayTitle,
              name: entry.title,
              author: {
                '@type': entry.category === 'asi-original' ? 'ResearchOrganization' : 'Organization',
                name: entry.authors,
                url: entry.category === 'asi-original' ? 'https://aistudies.institute' : undefined,
              },
              datePublished: entry.date,
              dateModified: entry.date,
              publicationStatus: entry.status,
              abstract: entry.abstract,
              description: entry.abstract,
              keywords: entry.tags.join(', '),
              about: entry.tags.map(tag => ({
                '@type': 'Thing',
                name: tag,
              })),
              inLanguage: 'en',
              isPartOf: {
                '@type': 'ResearchOrganization',
                name: 'AI Studies Institute Research Library',
              },
              publisher: entry.category === 'asi-original'
                ? {
                    '@type': 'ResearchOrganization',
                    name: 'AI Studies Institute',
                  }
                : {
                    '@type': 'Organization',
                    name: entry.authors,
                  },
              provider: {
                '@type': 'ResearchOrganization',
                name: 'AI Studies Institute',
              },
              url: `https://aistudies.institute/#research-${entry.id}`,
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://aistudies.institute/#research-${entry.id}`,
              },
              position: index + 1,
            }),
          }}
        />
      ))}

      <div className="container-asi">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8, ease: customEasing.smooth }}
          className="mb-10 grid gap-5 md:grid-cols-[0.8fr_1fr] md:items-end"
        >
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-asi-copper">
              Evidence Base
            </p>
            <h2 className="font-serif text-3xl text-asi-navy md:text-4xl">
              Research Library
            </h2>
          </div>
          <p className="mb-0 max-w-3xl text-slate-600 leading-7">
            Curated empirical research, institutional reports, and ASI original
            synthesis on human-AI collaboration, productivity outcomes,
            implementation failure, robotics, and high-stakes AI use.
          </p>
        </motion.div>

        <div className="mb-10 border border-slate-200 bg-white p-5 md:p-6">
          <div className="mb-5 flex flex-col gap-2 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
            <h3 className="font-serif text-xl text-asi-navy">
              Selected ASI Research
            </h3>
            <button
              onClick={() => handleCategoryChange('asi-original')}
              className="text-left text-sm font-semibold uppercase tracking-wide text-asi-copper hover:text-asi-navy md:text-right"
            >
              View ASI originals
            </button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {selectedOriginals.map((entry) => (
              <article
                key={entry.id}
                className="border border-slate-200 bg-[#fbfbfa] p-4 transition-colors hover:border-asi-copper"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-asi-copper">
                    ASI Original
                  </span>
                  <span className="text-xs text-slate-500">{entry.date}</span>
                </div>
                <h4 className="mb-2 font-serif text-lg leading-snug text-asi-navy">
                  {entry.displayTitle}
                </h4>
                <p className="line-clamp-4 text-sm leading-6 text-slate-600">
                  {entry.asiAnalysis ?? entry.abstract}
                </p>
                {entry.articleUrl && (
                  <a
                    href={entry.articleUrl}
                    className="mt-4 inline-flex text-sm font-semibold text-asi-navy hover:text-asi-copper"
                  >
                    Read research brief
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1, ease: customEasing.smooth }}
          className="mb-8"
        >
          <div className="relative max-w-2xl" role="search">
            <input
              type="text"
              placeholder="Search research, risks, sectors, or findings..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full border border-slate-300 bg-white px-4 py-3 pl-12 pr-10 text-slate-700 placeholder-slate-400 transition-all focus:border-asi-copper focus:outline-none focus:ring-2 focus:ring-asi-copper/20"
              aria-label="Search research"
            />
            <svg
              className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchQuery && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </motion.div>

        {/* Category Filter - Horizontal scroll on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2, ease: customEasing.smooth }}
          className="mb-8"
        >
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide">
            {categories.map((cat) => (
              <motion.button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`flex-shrink-0 border px-3.5 py-2 text-xs font-semibold uppercase tracking-wide transition-colors whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'border-asi-navy bg-asi-navy text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-asi-copper hover:text-asi-copper'
                }`}
              >
                {cat.label}
                <span className={`ml-2 text-xs ${activeCategory === cat.id ? 'text-white/70' : 'text-slate-400'}`}>
                  {cat.count}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {isBrowseMode ? (
          <div className="grid gap-5 md:grid-cols-2">
            {browseSections.map(({ category, entries }) => (
              <motion.section
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.45, ease: customEasing.smooth }}
                className="border border-slate-200 bg-white p-5"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-5">
                  <h3 className="font-serif text-xl text-asi-navy">
                    {category.label}
                  </h3>
                  <button
                    onClick={() => handleCategoryChange(category.id)}
                    className="text-left text-xs font-semibold uppercase tracking-wide text-asi-copper transition-colors hover:text-asi-navy md:text-right"
                  >
                    View all {category.count}
                  </button>
                </div>

                <ul className="space-y-3">
                  {entries.map((entry) => (
                    <li key={entry.id} className="border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
                      <button
                        onClick={() => {
                          handleSearchChange(entry.displayTitle);
                          handleCategoryChange(category.id);
                        }}
                        className="w-full text-left group"
                      >
                        <p className="font-serif text-base text-asi-navy group-hover:text-asi-copper transition-colors leading-snug">
                          {entry.displayTitle}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {entry.authors} / {entry.date}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.section>
            ))}
          </div>
        ) : (
          <>
            {/* Results Count */}
            <motion.div
              key={`${activeCategory}-${searchQuery}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 text-sm text-slate-500"
              role="status"
              aria-live="polite"
            >
              Showing {visibleResearch.length} of {filteredResearch.length} {filteredResearch.length === 1 ? 'study' : 'studies'}
              {activeCategory !== 'all' && ` in ${categories.find(c => c.id === activeCategory)?.label}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </motion.div>

            {/* Research Cards */}
            <motion.div
              className="space-y-6"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              key={activeCategory + searchQuery}
            >
              {visibleResearch.map((entry) => (
                <motion.article
                  key={entry.id}
                  variants={cardVariants}
                  className={`group overflow-hidden border border-slate-200 bg-white transition-colors duration-300 hover:border-asi-copper ${entry.articleUrl ? 'cursor-pointer' : ''}`}
                  whileHover={{ y: -2 }}
                  onClick={() => entry.articleUrl && window.open(entry.articleUrl, '_blank')}
                >
                  {/* Card Header */}
                  <div className="p-5 md:p-6">
                    {/* Tags Row */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {entry.category === 'asi-original' ? (
                        <span className="inline-flex items-center border border-asi-navy bg-asi-navy px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                          ASI Original
                        </span>
                      ) : (
                        <span className="inline-flex items-center border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                          External
                        </span>
                      )}
                      <span className="text-xs text-slate-400">
                        {entry.status} / {entry.date}
                      </span>
                      {entry.articleUrl && (
                        <span className="ml-auto inline-flex items-center border border-asi-copper/30 bg-asi-copper/10 px-2 py-0.5 text-xs font-medium text-asi-copper">
                          Summary available
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="font-serif text-lg md:text-xl lg:text-2xl text-asi-navy leading-snug mb-3 group-hover:text-asi-copper transition-colors">
                      {entry.displayTitle}
                    </h3>

                    {/* Authors */}
                    <p className="text-sm text-slate-500 mb-4">{entry.authors}</p>

                    {/* Abstract - Truncated on mobile */}
                    <div className="prose prose-gray max-w-none mb-5">
                      <p className="text-slate-700 leading-relaxed text-sm md:text-base line-clamp-4 md:line-clamp-none">
                        {entry.abstract}
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-5">
                      {entry.tags.slice(0, 4).map((tag) => (
                        <motion.span
                          key={tag}
                          className="border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
                        >
                          #{tag}
                        </motion.span>
                      ))}
                      {entry.tags.length > 4 && (
                        <span className="px-2.5 py-1 text-xs text-slate-400">+{entry.tags.length - 4}</span>
                      )}
                    </div>

                    {/* Action Links */}
                    <div className="mt-5 pt-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      {entry.articleUrl ? (
                        <span className="inline-flex items-center text-sm font-semibold text-asi-copper group-hover:underline">
                          Read summary
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-sm font-medium text-asi-navy">
                          Full source material summarized on this page
                        </span>
                      )}

                      {getSourceReferenceUrl(entry) && (
                        <a
                          href={getSourceReferenceUrl(entry)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-slate-400 hover:text-asi-copper transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {isDoi(getSourceReferenceUrl(entry)) ? 'DOI ↗' : 'Source reference ↗'}
                        </a>
                      )}

                      {/* Category Tag */}
                      <span className="text-xs text-gray-400 uppercase tracking-wide">
                        {categories.find(c => c.id === entry.category)?.label ?? entry.category}
                      </span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </motion.div>

            {hasMoreResults && (
              <div className="mt-8 flex justify-center">
                <motion.button
                  onClick={() => setVisibleCount((prev) => prev + pageSize)}
                  className="inline-flex items-center border border-slate-300 bg-white px-6 py-3 text-sm font-semibold uppercase tracking-wide text-asi-navy transition-colors hover:border-asi-copper hover:text-asi-copper"
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Load 8 more studies
                </motion.button>
              </div>
            )}

            {/* Empty State */}
            {filteredResearch.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
              >
                <p className="text-slate-500 mb-4">No research found matching your criteria.</p>
                <button
                  onClick={clearFilters}
                  className="text-asi-copper hover:text-asi-navy font-medium transition-colors"
                >
                  Clear filters
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default ResearchLibrary;
